/**
 * Las fechas del negocio, en hora de Nicaragua.
 *
 * A day in this app is a day at the shop, and the shop is in Nicaragua, six hours behind UTC.
 * Dates used to be cut from `toISOString()`, which is the date in Greenwich: from six in the
 * evening every sale, expense and count was stamped tomorrow, «Vendido hoy» dropped the day's
 * sales at dinner time, and the last evening of a month went into the next month's books.
 *
 * The zone is named rather than read off the device. Every phone and the server have to agree
 * on which day a sale belongs to, because the books are summed by it, and an owner reading them
 * on a phone set to Miami must see the same days the shop does.
 *
 * Instants stay instants: `clientTs`, `serverTs` and the like are moments in time, UTC, and
 * correct as they are. This is only for calendar dates, `YYYY-MM-DD`.
 */

export const ZONA_HORARIA = 'America/Managua';

const partes = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_HORARIA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The shop's calendar date at an instant, as `YYYY-MM-DD`.
 *
 * Assembled from parts rather than from a locale's date format, which ICU is free to change.
 */
export function fechaEnNicaragua(instante: Date | string): string {
  const p = partes.formatToParts(typeof instante === 'string' ? new Date(instante) : instante);
  const v = (tipo: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === tipo)!.value;
  return `${v('year')}-${v('month')}-${v('day')}`;
}

/** Today at the shop. */
export function hoy(): string {
  return fechaEnNicaragua(new Date());
}

/**
 * The calendar date `n` days after `fecha` (before it, for a negative `n`).
 *
 * Arithmetic on the date itself, at UTC midnight where no zone can move it, so the answer is a
 * calendar day and never depends on the hour somebody asked.
 */
export function sumarDias(fecha: string, n: number): string {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from one `YYYY-MM-DD` to another: negative when `hasta` comes first. */
export function diasEntre(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T12:00:00Z`) - Date.parse(`${desde}T12:00:00Z`)) / 86_400_000);
}
